import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { PortalLayout } from "@/components/PortalLayout";
import { SmartRedirect } from "@/components/SmartRedirect";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Projects = lazyWithRetry(() => import("./pages/Projects"));
const Clients = lazyWithRetry(() => import("./pages/Clients"));
const Users = lazyWithRetry(() => import("./pages/Users"));
const Invoices = lazyWithRetry(() => import("./pages/Invoices"));
const Quotes = lazyWithRetry(() => import("./pages/Quotes"));
const Subscriptions = lazyWithRetry(() => import("./pages/Subscriptions"));
const Inventory = lazyWithRetry(() => import("./pages/Inventory"));

const ClientDashboard = lazyWithRetry(() => import("./pages/ClientDashboard"));
const ProjectDetail = lazyWithRetry(() => import("./pages/ProjectDetail"));
const ClientPortal = lazyWithRetry(() => import("./pages/ClientPortal"));
const ClientSlugPortal = lazyWithRetry(() => import("./pages/ClientSlugPortal"));
const Files = lazyWithRetry(() => import("./pages/Files"));
const ProfileSettings = lazyWithRetry(() => import("./pages/ProfileSettings"));
const SystemSettings = lazyWithRetry(() => import("./pages/SystemSettings"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));

const queryClient = new QueryClient();

const App = () => {
  return (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
          <Routes>
            <Route path="/" element={<SmartRedirect />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/portal" element={<PortalLayout><ClientPortal /></PortalLayout>} />
            <Route path="/dashboard" element={<PortalLayout><Dashboard /></PortalLayout>} />
            <Route path="/projects" element={<PortalLayout><Projects /></PortalLayout>} />
            <Route path="/invoices" element={<PortalLayout><Invoices /></PortalLayout>} />
            <Route path="/quotes" element={<PortalLayout><Quotes /></PortalLayout>} />
            <Route path="/subscriptions" element={<PortalLayout><Subscriptions /></PortalLayout>} />
            <Route path="/inventory" element={<PortalLayout><Inventory /></PortalLayout>} />
            <Route path="/clients" element={<PortalLayout><Clients /></PortalLayout>} />

            <Route path="/clients/:clientId" element={<PortalLayout><ClientDashboard /></PortalLayout>} />
            <Route path="/clients/:clientId/projects/:projectId" element={<PortalLayout><ProjectDetail /></PortalLayout>} />
            <Route path="/users" element={<PortalLayout><Users /></PortalLayout>} />
            <Route path="/messages" element={<PortalLayout><Messages /></PortalLayout>} />
            <Route path="/files" element={<PortalLayout><Files /></PortalLayout>} />
            <Route path="/settings" element={<PortalLayout><ProfileSettings /></PortalLayout>} />
            <Route path="/system-settings" element={<PortalLayout><SystemSettings /></PortalLayout>} />
            {/* Client slug portal routes - must come last as catch-all */}
            <Route path="/:clientSlug" element={<ClientSlugPortal />} />
            <Route path="/:clientSlug/*" element={<ClientSlugPortal />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  );
};

export default App;